import { Component, computed, DestroyRef, effect, inject, input, OnInit, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OuiSelectOption } from 'omnium-ui/form-field';
import { SHARED_IMPORTS } from 'projects/minotaure-lib/src/shared-imports';
import { FiltreBuilder } from '@lib/services/filtrer-investisseurs/filtre-builders/filtre-builder.model';
import { FiltreLine } from '@lib/services/filtrer-investisseurs/filtre-lines/filtre-line.model';

type ButtonLineOption = {
  value: any;
  label: string;
  isSelected: boolean;
};

@Component({
  selector: 'app-filtre-button-line',
  imports: [SHARED_IMPORTS],
  templateUrl: './filtre-button-line.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './filtre-button-line.component.scss',
})
export class FiltreButtonLineComponent implements OnInit {
  builder = input.required<FiltreBuilder>();
  line = input.required<FiltreLine>();
  last = input<boolean>(false);
  index = input.required<number>();

  lineChange = output<{ index: number; value: any[] }>();

  buttonOptions = signal<ButtonLineOption[]>([]);
  title = '';
  destroyRef = inject(DestroyRef);
  selectedValues = computed(() =>
    this.buttonOptions()
      .filter(opt => opt.isSelected)
      .map(opt => opt.value)
  );

  constructor() {
    effect(() => this.lineChange.emit({ index: this.index(), value: this.selectedValues() }));
  }
  ngOnInit(): void {
    this.builder()
      .options?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options: OuiSelectOption<any>[] | null) => {
        const optionsNotNull = options ?? [];
        this.buttonOptions.set(
          optionsNotNull.map(opt => ({
            value: opt.value,
            label: opt.label,
            isSelected: this.isButtonSelected(opt, this.line()),
          }))
        );
      });
    this.title = FiltreBuilder.getBuilderLibelle(this.builder().getBuilderCode());
  }

  toggleButtonStatut(option: ButtonLineOption) {
    this.buttonOptions.update(opts =>
      opts.map(o => ({
        ...o,
        isSelected: o.value === option.value ? !o.isSelected : o.isSelected,
      }))
    );
  }

  updateValueLabel(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '';
      }
      const labels = this.buttonOptions()
        .filter(opt => value.includes(opt.value))
        .map(opt => opt.label);
      return labels.join(', ');
    }
    const selectedOption = this.buttonOptions().find(opt => opt.value === value);
    return selectedOption?.label ?? value;
  }

  clearSelection() {
    this.buttonOptions.update(opts => opts.map(opt => ({ ...opt, isSelected: false })));
  }

  private isButtonSelected(option: OuiSelectOption, line: FiltreLine): boolean {
    if (line.value) {
      if (Array.isArray(line.value)) {
        if (line.value.every(v => Array.isArray(v))) {
          const sameArray = (a: string[], b: string[]) => a.length === b.length && a.every(v => b.includes(v));
          return line.value.some(v => sameArray(v, option.value));
        } else {
          return line.value.includes(option.value);
        }
      }
      return line.value === option.value;
    }
    return false;
  }
}
